import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { cn } from '@/lib/utils';

export type MediaSectionPage =
  | 'images'
  | 'floor-plans'
  | 'spherical-images'
  | 'focus-points'
  | 'digital-twins'
  | 'videos';

const SECTION_TITLE_KEYS: Record<MediaSectionPage, string> = {
  images: 'images',
  'floor-plans': 'floorPlans',
  'spherical-images': '360Images',
  'focus-points': 'focus-points',
  'digital-twins': '3D-models',
  videos: 'videos',
};

type MediaSectionHeaderProps = {
  type: 'estate' | 'project';
  id: string;
  page: string;
  className?: string;
  // right side slot, for the section actions
  children?: ReactNode;
};

const BACK_LINK_CLASS =
  'inline-flex shrink-0 items-center gap-1.5 rounded-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export const MediaSectionHeader = ({
  type,
  id,
  page,
  className,
  children,
}: MediaSectionHeaderProps) => {
  const { t } = useTranslation();
  const titleKey = SECTION_TITLE_KEYS[page as MediaSectionPage] ?? page;

  const backContent = (
    <>
      <ArrowLeft className="h-4 w-4" />
      {t('media')}
    </>
  );

  return (
    <div className={cn('new-ui flex flex-wrap items-center justify-between gap-2', className)}>
      <div className="flex min-w-0 items-center gap-2 text-sm">
        {/* A plain breadcrumb link: a ghost button's padding put the separator off center */}
        {type === 'estate' ? (
          <Link
            to="/estates/$estateId/media"
            params={{ estateId: id }}
            aria-label={t('backToMedia')}
            className={BACK_LINK_CLASS}
          >
            {backContent}
          </Link>
        ) : (
          <Link
            to="/projects/$projectId/media"
            params={{ projectId: id }}
            aria-label={t('backToMedia')}
            className={BACK_LINK_CLASS}
          >
            {backContent}
          </Link>
        )}
        <span className="text-muted-foreground" aria-hidden="true">
          /
        </span>
        <span className="truncate font-medium text-foreground">{t(titleKey)}</span>
      </div>
      {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  );
};
